import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { initialToastState, toastReducer, type IToast, type ToastType } from './toastReducer';

/**
 * MOPS toast infrastructure. The operator-tuned replacement for `window.alert()`
 * — the playbook in `packages/mops-web/AGENTS.md` § "Feedback and latency"
 * specifies top-right toasts with 4s auto-dismiss on success/info and
 * click-to-dismiss on errors so a failure is not silently swallowed.
 *
 * Three pieces work together:
 *
 * - {@link ToastProvider} mounts the queue + viewport once at the app root
 *   (`MainApp.tsx`), inside the auth shell so toasts can outlive a single
 *   route transition.
 * - {@link useToast} is the consumer-facing hook. Scenes call
 *   `showToast({ type, message })` from mutation `onCompleted` / `catch`
 *   handlers; no per-scene mounting required.
 * - The viewport renders opaque elevated panels with a 1px accent border in
 *   the type color (success / error / info), matching the MOPS aesthetic
 *   ("borders over backgrounds, no shadows, sharp edges"). See the comment
 *   on `bg-gray-light-700` below for the palette-stop rationale.
 *
 * The queue itself is a pure reducer in `toastReducer.ts` so the cap and
 * ordering semantics can be exercised in vitest's `node` environment.
 */

const SUCCESS_INFO_AUTO_DISMISS_MS = 4000;

interface IToastInput {
  type: ToastType;
  message: string;
}

interface IToastContextValue {
  showToast: (input: IToastInput) => string;
  dismissToast: (id: string) => void;
}

const ToastContext = React.createContext<IToastContextValue | undefined>(undefined);

interface IToastProviderProps {
  children: React.ReactNode;
}

/**
 * Mounts the toast queue + viewport for the entire app. Wrap the auth shell so
 * every authenticated scene can call {@link useToast}; the viewport renders
 * top-right and survives route transitions because the provider sits above
 * `<Routes>` in `MainApp.tsx`.
 */
const ToastProvider = ({ children }: IToastProviderProps) => {
  const [state, dispatch] = React.useReducer(toastReducer, initialToastState);
  const idCounterRef = React.useRef(0);
  const timeoutsRef = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = React.useCallback((id: string) => {
    const timeout = timeoutsRef.current.get(id);
    if (timeout !== undefined) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
    dispatch({ type: 'dismiss', id });
  }, []);

  const showToast = React.useCallback(
    ({ type, message }: IToastInput): string => {
      idCounterRef.current += 1;
      const id = `toast-${idCounterRef.current}`;
      dispatch({ type: 'show', toast: { id, type, message } });
      // Errors stay until the operator dismisses — silent timeouts on failures
      // are exactly what the alert() replacement is meant to fix.
      if (type !== 'error') {
        const timeout = setTimeout(() => dismissToast(id), SUCCESS_INFO_AUTO_DISMISS_MS);
        timeoutsRef.current.set(id, timeout);
      }
      return id;
    },
    [dismissToast]
  );

  React.useEffect(() => {
    // Capture the current timeouts map for cleanup so the lint rule about
    // ref values inside the cleanup function is satisfied.
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach((t) => clearTimeout(t));
      timeouts.clear();
    };
  }, []);

  const value = React.useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={state.toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

/**
 * Hook for scenes to push toasts. Throws if called outside `ToastProvider`
 * — providers wrap the entire authenticated app, so a missing context here
 * is a wiring bug, not a runtime condition worth handling.
 */
export const useToast = (): IToastContextValue => {
  const ctx = React.useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};

const TYPE_BORDER: Record<ToastType, string> = {
  success: 'border-fg-success-primary',
  error: 'border-fg-error-primary',
  info: 'border-border-primary',
};

const TYPE_LABEL: Record<ToastType, string> = {
  success: 'Success',
  error: 'Error',
  info: 'Notice',
};

interface IToastViewportProps {
  toasts: IToast[];
  onDismiss: (id: string) => void;
}

const ToastViewport = ({ toasts, onDismiss }: IToastViewportProps) => {
  if (toasts.length === 0) {
    return null;
  }
  return (
    <div className="pointer-events-none fixed right-4 top-16 z-50 flex w-80 flex-col gap-2">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

interface IToastItemProps {
  toast: IToast;
  onDismiss: (id: string) => void;
}

const ToastItem = ({ toast, onDismiss }: IToastItemProps) => {
  const handleDismiss = () => onDismiss(toast.id);
  return (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      className={cx(
        // Use the gray-light-700 palette stop (#2a3e45) directly: dark mode's
        // `bg-bg-*` semantic tokens are alpha-based (`#ffffff14` and lighter)
        // which render visibly translucent over the body bg, and the body
        // (`#0e1b20`) plus the next-darkest stop (`gray-light-800`, `#1a2b31`)
        // are too close in value to read as an elevated surface. A toast needs
        // unambiguous opacity, and there is no opaque semantic-token equivalent
        // in the current dark-mode palette.
        'bg-gray-light-700 text-fg-primary pointer-events-auto flex items-start gap-3 border px-3 py-2 text-sm',
        TYPE_BORDER[toast.type]
      )}
    >
      <span
        className={cx(
          'shrink-0 text-xs font-medium uppercase tracking-wider',
          toast.type === 'success' && 'text-fg-success-primary',
          toast.type === 'error' && 'text-fg-error-primary',
          toast.type === 'info' && 'text-fg-secondary'
        )}
      >
        {TYPE_LABEL[toast.type]}
      </span>
      <span className="text-fg-primary flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss notification"
        className="text-fg-tertiary hover:text-fg-primary focus-visible:outline-border-brand shrink-0 cursor-pointer text-xs uppercase tracking-wider focus-visible:outline-2 focus-visible:-outline-offset-1"
      >
        Dismiss
      </button>
    </div>
  );
};

export default ToastProvider;
